import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { LoadMissingConsents, SetHasMissingMandatoryConsent } from '../../+state';
import { ConsentType } from '../../models';
import { ConsentRoutingService } from '../consent';

@Injectable()
export class ConsentInterceptor implements HttpInterceptor {
  private consentFlowTriggered = false;

  constructor(
    private router: Router,
    private store: Store,
    private consentRoutingService: ConsentRoutingService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const isConsentRoute = event.urlAfterRedirects.includes('/consent/flow');
        if (!isConsentRoute && this.consentFlowTriggered) {
          this.consentFlowTriggered = false;
        }
      }
    });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            if (req.url.indexOf('/graphql') > -1 && event.body?.errors) {
              this.handleGraphQLConsentErrors(event.body.errors);
            }
          }
        },
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleGraphQLConsentErrors(errors: any[]): void {
    const consentErrors = errors.filter(error => this.isGraphQLConsentError(error));

    if (consentErrors.length > 0) {
      this.triggerConsentFlow();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isGraphQLConsentError(error: any): boolean {
    // Check for ConsentType enum values in error messages or codes
    const consentTypes = Object.values(ConsentType);

    return (
      error.message?.includes('MANDATORY_CONSENT_FOR_USER_IS_MISSING') ||
      consentTypes.some(
        consentType =>
          error.message?.includes(consentType) ||
          error.extensions?.code?.includes(consentType) ||
          error.extensions?.consentType === consentType
      )
    );
  }

  private triggerConsentFlow(): void {
    if (this.consentFlowTriggered) {
      return;
    }

    if (this.consentRoutingService.isConsentFlowRoute() || this.isAuthRoute()) {
      return;
    }

    const currentRoute = this.getCurrentRoute();
    this.consentFlowTriggered = true;

    this.store.dispatch(SetHasMissingMandatoryConsent({ hasMissingMandatoryConsent: true }));
    this.store.dispatch(LoadMissingConsents());

    // Navigate to consent flow with return route information
    this.consentRoutingService.navigateToConsentFlow(
      currentRoute,
      this.router.routerState.root.queryParams
    );
  }

  private getCurrentRoute(): string {
    const urlTree = this.router.parseUrl(this.router.url);
    return urlTree.root.children.primary?.segments.map(s => s.path).join('/') || '/content';
  }

  private isAuthRoute(): boolean {
    return this.router.url.includes('/auth');
  }
}
